import type { StateCreator } from 'zustand';
import type {
  DraftMetric,
  FilterStep,
  ReferenceScope,
  ReferenceStatistic,
  RetentionMethod,
  SecuritySelectionStore,
  SelectionMethod,
  ThresholdOperator,
  TurnoverControl,
} from '../types';
import type {
  ComparisonType,
  MissingValuePolicy,
  RankingDirection,
  RankingMethod,
  SelectionSlice,
  ThresholdMissingPolicy,
  TieBreakMetric,
  TieBreakPreference,
  TieHandling,
} from '@/features/Strategies/stores/types/definitionFormStoreTypes';

// NOTE: Selection and threshold fields are flat for now. A future refactor
// should group them into `draftSelection: { method, logic, value, ... }` and
// `draftThreshold: { operator, value, ... }` objects for cleaner resets.
// See SECURITY_SELECTION.md Fix 2 for the intended structure.

/**
 * The handle a formula uses for a metric row: `M1`, `M2`, `M3`…
 *
 * These are the SAME codes the Linear and Product modes already show in the
 * badge column, so the label a user reads on a row does not change meaning when
 * they switch combination — it is one identity, shown three ways.
 *
 * Deliberately NOT derived from the metric name. The case that forced aliases
 * to exist is VALMO's momentum leg — one metric at three lookbacks — and a
 * derived name gives `cumulative_return_3m`, `cumulative_return_3m_2`,
 * `cumulative_return_3m_3`: three near-identical strings that are long enough
 * to truncate in the column and impossible to tell apart at a glance. `M1 / M4`
 * reads better than `cumulative_returns_3m / volatility_6m` precisely because
 * the row above it says what M1 is.
 *
 * A code is assigned once and never reassigned to a different metric. That is
 * the load-bearing rule: if reordering rows renumbered them, `M1` in a saved
 * formula would silently come to mean a different metric — a change in what the
 * strategy computes, with nothing on screen to show for it. So the badge
 * travels with its row when the table is reordered, and a freed code is only
 * reused when the current formula does not mention it.
 */
function nextFreeCode(metrics: Array<DraftMetric>, formula: string): string {
  const taken = new Set(metrics.map((m) => m.alias).filter(Boolean));

  // First pass: a code that is neither in use nor named by the expression.
  // Reusing one the formula still mentions would rebind it to a different
  // metric behind the user's back — the rule this whole scheme exists for.
  for (let i = 1; i <= metrics.length + 1; i++) {
    const code = `M${i}`;
    if (!taken.has(code) && !referencesCode(formula, code)) return code;
  }

  // Second pass: every code up to n+1 was taken OR named, so keep counting.
  // The bound used to be `n+1` with `M${n+1}` as the fallback — which is a code
  // the loop had just rejected, so it could be handed out a SECOND time. Two
  // rows sharing a code makes the expression ambiguous about which metric it
  // means; the engine refuses the step, and the editor showed nothing.
  for (let i = metrics.length + 2; i <= metrics.length * 2 + 2; i++) {
    const code = `M${i}`;
    if (!taken.has(code) && !referencesCode(formula, code)) return code;
  }

  // Unreachable in practice — the range above is strictly larger than the
  // number of rows — but a duplicate is worse than a strange name, so the last
  // resort is uniqueness rather than a shape.
  let n = metrics.length * 2 + 3;
  while (taken.has(`M${n}`)) n++;
  return `M${n}`;
}

/**
 * Whether `formula` names `code` as a whole word — `M1` must not match `M12`.
 *
 * Exported because the table needs the same answer to warn before a removal
 * orphans the expression, and two regexes for one question would eventually
 * disagree.
 */
export function referencesCode(formula: string, code: string): boolean {
  if (!code) return false;
  // Escaped: a strategy saved before M-codes carries a semantic alias, and one
  // containing a regex metacharacter would throw during render rather than
  // answer the question.
  const escaped = code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\b${escaped}\\b`).test(formula);
}

/**
 * Give every row a code, keeping the ones already assigned.
 *
 * Only fills gaps — an existing code is never rewritten, because a formula may
 * already refer to it.
 */
function assignMissingAliases(
  metrics: Array<DraftMetric>,
  formula: string,
): void {
  for (const m of metrics) {
    if (!m.alias?.trim()) m.alias = nextFreeCode(metrics, formula);
  }
}

/** A code the numbering scheme owns — `M` followed by digits, nothing else. */
const CODE_SHAPE = /^M\d+$/;

/** Every whole-word `M<digits>` the expression names. */
function codesNamedBy(formula: string): Set<string> {
  return new Set(formula.match(/\bM\d+\b/g) ?? []);
}

/**
 * Renumber the M-codes to follow row order, and rewrite the expression so that
 * every reference still lands on the metric it named before.
 *
 * The codes were deliberately permanent: assigned once, never reassigned,
 * because a saved formula saying `M1` must not silently come to mean a
 * different metric. Dragging a row to a new rank makes position and code
 * disagree, so one of them has to give — either the badge lies about the row's
 * rank, or the code follows the row and the text follows the code.
 *
 * This does the second, which is a symbol rename rather than a rebind. Swapping
 * the two rows of `z(M1) - M2` yields `z(M2) - M1`: the same two metrics, in the
 * same two places, computing exactly what it computed before. The badge and the
 * expression never disagree, and the strategy is unchanged.
 *
 * Three things this has to get right:
 *
 * 1. **Substitution is simultaneous.** Applied one code at a time it cascades —
 *    rewriting `M1`→`M2` and then `M2`→`M3` turns the original `M1` into `M3`.
 * 2. **Non-code aliases are left alone.** A hand-written `value` is the user's
 *    name for that metric; reordering rows is not a request to rename it. Such a
 *    row keeps its alias and does not consume a number.
 * 3. **A dangling reference stays dangling.** If the formula names `M3` and no
 *    row carries it (the row was removed), `M3` is skipped by the numbering.
 *    Handing it to another row would bind a broken reference to a real metric —
 *    silently, and to the wrong one. Better it stays broken and visible.
 */
export function renumberCodes(
  aliases: Array<string | undefined>,
  formula: string,
): { aliases: Array<string | undefined>; formula: string } {
  const carried = new Set(aliases.filter((a): a is string => Boolean(a)));
  // Named by the text but carried by no row: a reference to a metric that is
  // gone. Off-limits as a new code.
  const dangling = new Set(
    [...codesNamedBy(formula)].filter((c) => !carried.has(c)),
  );

  const next: Array<string | undefined> = [];
  const rename = new Map<string, string>();
  let n = 0;
  for (const alias of aliases) {
    if (!alias || !CODE_SHAPE.test(alias)) {
      next.push(alias); // custom name — untouched, and it costs no number
      continue;
    }
    let code: string;
    do {
      n += 1;
      code = `M${n}`;
    } while (dangling.has(code));
    next.push(code);
    if (code !== alias) rename.set(alias, code);
  }

  if (rename.size === 0) return { aliases: next, formula };

  // One pass, one lookup per match — the simultaneity of point 1.
  const rewritten = formula.replace(/\bM\d+\b/g, (m) => rename.get(m) ?? m);
  return { aliases: next, formula: rewritten };
}

const DRAFT_DEFAULTS = {
  draftCombinationMethod: 'sum' as const,
  draftFormula: '',
  draftSelectionMethod: 'fixed' as const,
  draftSelectionLogic: 'highest' as const,
  draftSelectionValue: 100,
  draftRankingDirection: 'higher' as const,
  draftSelectionSlice: 'top' as const,
  draftUpperCutoff: 100,
  draftRankingMethod: 'sequential_rank' as const,
  draftTieHandling: 'include_all_ties' as const,
  draftTieBreakMetric: 'market_cap' as const,
  draftTieBreakPreference: 'highest' as const,
  draftMissingValuePolicy: 'exclude_from_ranking' as const,
  draftThresholdOperator: '>' as const,
  draftThresholdValue: 0,
  draftComparisonType: 'fixed_threshold' as const,
  draftThresholdMissingPolicy: 'exclude_from_filtering' as const,
  // Per-step turnover control (Selection Rules → Advanced Settings).
  draftTurnoverControl: 'None' as const,
  draftBufferSize: '',
  draftRetentionMethod: 'Replace Incumbents' as const,
  // Per-step overlapping-holding ("legs"), sibling of the turnover buffer.
  draftHoldingMethod: 'None' as const,
  draftHoldingPeriods: '',
  draftReferenceScope: 'eligible_universe' as const,
  draftReferenceStatistic: 'median' as const,
};

export interface DraftSlice {
  draftMetrics: Array<DraftMetric>;
  draftCombinationMethod: 'sum' | 'product' | 'formula';
  /** The expression, when the step combines by formula. */
  draftFormula: string;
  draftSelectionMethod: SelectionMethod;
  draftSelectionLogic: 'highest' | 'lowest';
  draftSelectionValue: number;
  draftRankingDirection: RankingDirection;
  draftSelectionSlice: SelectionSlice;
  draftUpperCutoff: number;
  draftRankingMethod: RankingMethod;
  draftTieHandling: TieHandling;
  draftTieBreakMetric: TieBreakMetric;
  draftTieBreakPreference: TieBreakPreference;
  draftMissingValuePolicy: MissingValuePolicy;
  draftThresholdOperator: ThresholdOperator;
  draftThresholdValue: number;
  draftComparisonType: ComparisonType;
  draftThresholdMissingPolicy: ThresholdMissingPolicy;
  draftTurnoverControl: TurnoverControl;
  draftBufferSize: string;
  draftRetentionMethod: RetentionMethod;
  draftHoldingMethod: 'None' | 'Overlapping';
  draftHoldingPeriods: string;
  draftReferenceScope: ReferenceScope;
  draftReferenceStatistic: ReferenceStatistic;
  addDraftMetric: (metric: Omit<DraftMetric, 'id'>) => void;
  removeDraftMetric: (metricId: string) => void;
  reorderDraftMetrics: (from: number, to: number) => void;
  updateDraftMetricById: (
    metricId: string,
    updates: Partial<Omit<DraftMetric, 'id'>>,
  ) => void;
  updateDraftMetric: (
    metricName: string,
    updates: Partial<Omit<DraftMetric, 'id'>>,
  ) => void;
  normalizeWeights: () => void;
  setDraftCombinationMethod: (method: 'sum' | 'product' | 'formula') => void;
  setDraftFormula: (formula: string) => void;
  setDraftSelectionMethod: (method: SelectionMethod) => void;
  setDraftSelectionLogic: (logic: 'highest' | 'lowest') => void;
  setDraftSelectionValue: (value: number) => void;
  setDraftRankingDirection: (dir: RankingDirection) => void;
  setDraftSelectionSlice: (slice: SelectionSlice) => void;
  setDraftUpperCutoff: (value: number) => void;
  setDraftRankingMethod: (method: RankingMethod) => void;
  setDraftTieHandling: (handling: TieHandling) => void;
  setDraftTieBreakMetric: (metric: TieBreakMetric) => void;
  setDraftTieBreakPreference: (pref: TieBreakPreference) => void;
  setDraftMissingValuePolicy: (policy: MissingValuePolicy) => void;
  setDraftThresholdOperator: (op: ThresholdOperator) => void;
  setDraftThresholdValue: (value: number) => void;
  setDraftComparisonType: (type: ComparisonType) => void;
  setDraftThresholdMissingPolicy: (policy: ThresholdMissingPolicy) => void;
  setDraftTurnoverControl: (v: TurnoverControl) => void;
  setDraftBufferSize: (v: string) => void;
  setDraftRetentionMethod: (v: RetentionMethod) => void;
  setDraftHoldingMethod: (v: 'None' | 'Overlapping') => void;
  setDraftHoldingPeriods: (v: string) => void;
  setDraftReferenceScope: (v: ReferenceScope) => void;
  setDraftReferenceStatistic: (v: ReferenceStatistic) => void;
  loadStepIntoDraft: (step: FilterStep) => void;
  resetDraft: () => void;
}

export const createDraftSlice: StateCreator<
  SecuritySelectionStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  DraftSlice
> = (set) => ({
  draftMetrics: [],
  ...DRAFT_DEFAULTS,

  addDraftMetric: (metric) =>
    set(
      (state) => {
        state.draftMetrics.push({ ...metric, id: crypto.randomUUID() });
        // A row added while the step is already in formula mode still needs a
        // handle; without one the expression could not name it and the step
        // would fail validation on save rather than here.
        if (state.draftCombinationMethod === 'formula') {
          assignMissingAliases(state.draftMetrics, state.draftFormula);
        }
        state.editingMetricName = null;
        state.editingMetricId = null;
      },
      false,
      'draft/addMetric',
    ),

  removeDraftMetric: (metricId) =>
    set(
      (state) => {
        state.draftMetrics = state.draftMetrics.filter(
          (m) => m.id !== metricId,
        );
        // The badge reads as the row's rank, so a gap left by a removal would
        // make it lie — [M1, M2, M3] minus the first would show M2 and M3 on
        // rows 1 and 2.
        //
        // This closes the gap only when it is safe to. The removed code is now
        // dangling if the expression still names it, and `renumberCodes` skips
        // dangling codes — so with a formula referring to the dead metric this
        // is a no-op, and the reference stays visibly broken instead of being
        // silently rebound onto a surviving row. It compacts when nothing
        // refers to it: an empty formula, or Linear mode.
        const renumbered = renumberCodes(
          state.draftMetrics.map((m) => m.alias),
          state.draftFormula,
        );
        state.draftMetrics.forEach((m, i) => {
          m.alias = renumbered.aliases[i];
        });
        state.draftFormula = renumbered.formula;
      },
      false,
      'draft/removeMetric',
    ),

  reorderDraftMetrics: (from, to) =>
    set(
      (state) => {
        const metrics = state.draftMetrics;
        if (
          from === to ||
          from < 0 ||
          to < 0 ||
          from >= metrics.length ||
          to >= metrics.length
        ) {
          return;
        }
        const [moved] = metrics.splice(from, 1);
        metrics.splice(to, 0, moved);

        // The rank IS the code, so the move renames — and the expression is
        // rewritten in the same commit. Doing it in two steps would leave a
        // render in between where the badges and the formula disagree.
        const renumbered = renumberCodes(
          metrics.map((m) => m.alias),
          state.draftFormula,
        );
        metrics.forEach((m, i) => {
          m.alias = renumbered.aliases[i];
        });
        state.draftFormula = renumbered.formula;
      },
      false,
      'draft/reorderMetrics',
    ),

  updateDraftMetricById: (metricId, updates) =>
    set(
      (state) => {
        const metric = state.draftMetrics.find((m) => m.id === metricId);
        if (metric) Object.assign(metric, updates);
      },
      false,
      'draft/updateMetricById',
    ),

  updateDraftMetric: (metricName, updates) =>
    set(
      (state) => {
        const metric = state.draftMetrics.find((m) => m.name === metricName);
        if (metric) Object.assign(metric, updates);
      },
      false,
      'draft/updateMetricByName',
    ),

  normalizeWeights: () =>
    set(
      (state) => {
        const count = state.draftMetrics.length;
        if (count === 0) return;
        // Clamp negatives before computing sum
        for (const m of state.draftMetrics) {
          m.weight = Math.max(0, m.weight);
        }
        const sum = state.draftMetrics.reduce((s, m) => s + m.weight, 0);
        if (sum === 0) {
          // All weights are 0 — fall back to equal division
          const equalWeight = +(100 / count).toFixed(2);
          for (let i = 0; i < count - 1; i++) {
            state.draftMetrics[i].weight = equalWeight;
          }
          state.draftMetrics[count - 1].weight = +(
            100 -
            equalWeight * (count - 1)
          ).toFixed(2);
        } else {
          // Proportional normalization — preserve relative weights
          let assigned = 0;
          for (let i = 0; i < count - 1; i++) {
            const w = +((state.draftMetrics[i].weight / sum) * 100).toFixed(2);
            state.draftMetrics[i].weight = w;
            assigned += w;
          }
          state.draftMetrics[count - 1].weight = +(100 - assigned).toFixed(2);
        }
      },
      false,
      'draft/normalizeWeights',
    ),

  setDraftCombinationMethod: (method) =>
    set(
      (state) => {
        state.draftCombinationMethod = method;
        if (method === 'formula') {
          // The formula carries the weights, so the schema forbids setting
          // them — the same rule as `product`. Leaving stale percentages on the
          // rows would show a "Total 100%" the backend then rejects.
          state.draftMetrics.forEach((m) => (m.weight = 0));
          assignMissingAliases(state.draftMetrics, state.draftFormula);
        } else if (method === 'product') {
          state.draftMetrics.forEach((m) => (m.weight = 0));
        } else {
          const count = state.draftMetrics.length;
          if (count > 0) {
            const equalWeight = +(100 / count).toFixed(2);
            for (let i = 0; i < count - 1; i++) {
              state.draftMetrics[i].weight = equalWeight;
            }
            state.draftMetrics[count - 1].weight = +(
              100 -
              equalWeight * (count - 1)
            ).toFixed(2);
          }
        }
      },
      false,
      'draft/setCombinationMethod',
    ),

  setDraftFormula: (formula) =>
    set(
      (state) => {
        state.draftFormula = formula;
      },
      false,
      'draft/setFormula',
    ),

  setDraftSelectionMethod: (method) =>
    set(
      (state) => {
        state.draftSelectionMethod = method;
      },
      false,
      'draft/setSelectionMethod',
    ),

  setDraftSelectionLogic: (logic) =>
    set(
      (state) => {
        state.draftSelectionLogic = logic;
      },
      false,
      'draft/setSelectionLogic',
    ),

  setDraftSelectionValue: (value) =>
    set(
      (state) => {
        state.draftSelectionValue = value;
      },
      false,
      'draft/setSelectionValue',
    ),

  setDraftRankingDirection: (dir) =>
    set(
      (state) => {
        state.draftRankingDirection = dir;
      },
      false,
      'draft/setRankingDirection',
    ),

  setDraftSelectionSlice: (slice) =>
    set(
      (state) => {
        state.draftSelectionSlice = slice;
      },
      false,
      'draft/setSelectionSlice',
    ),

  setDraftUpperCutoff: (value) =>
    set(
      (state) => {
        state.draftUpperCutoff = value;
      },
      false,
      'draft/setUpperCutoff',
    ),

  setDraftRankingMethod: (method) =>
    set(
      (state) => {
        state.draftRankingMethod = method;
        // Keep the redundant SelectionMethod ('fixed' | 'percentile') in lockstep.
        // The summary and backend export key off `method`, but this editor only
        // drives `rankingMethod` — without syncing, switching Sequential<->Percentile
        // never reaches the summary ("Top X securities" vs "Top X%") or the config.
        state.draftSelectionMethod =
          method === 'percentile_rank' ? 'percentile' : 'fixed';
      },
      false,
      'draft/setRankingMethod',
    ),

  setDraftTieHandling: (handling) =>
    set(
      (state) => {
        state.draftTieHandling = handling;
      },
      false,
      'draft/setTieHandling',
    ),

  setDraftTieBreakMetric: (metric) =>
    set(
      (state) => {
        state.draftTieBreakMetric = metric;
      },
      false,
      'draft/setTieBreakMetric',
    ),

  setDraftTieBreakPreference: (pref) =>
    set(
      (state) => {
        state.draftTieBreakPreference = pref;
      },
      false,
      'draft/setTieBreakPreference',
    ),

  setDraftMissingValuePolicy: (policy) =>
    set(
      (state) => {
        state.draftMissingValuePolicy = policy;
      },
      false,
      'draft/setMissingValuePolicy',
    ),

  setDraftThresholdOperator: (op) =>
    set(
      (state) => {
        state.draftThresholdOperator = op;
      },
      false,
      'draft/setThresholdOperator',
    ),

  setDraftThresholdValue: (value) =>
    set(
      (state) => {
        state.draftThresholdValue = value;
      },
      false,
      'draft/setThresholdValue',
    ),

  setDraftComparisonType: (type) =>
    set(
      (state) => {
        state.draftComparisonType = type;
      },
      false,
      'draft/setComparisonType',
    ),

  setDraftThresholdMissingPolicy: (policy) =>
    set(
      (state) => {
        state.draftThresholdMissingPolicy = policy;
      },
      false,
      'draft/setThresholdMissingPolicy',
    ),

  setDraftTurnoverControl: (v) =>
    set(
      (state) => {
        state.draftTurnoverControl = v;
      },
      false,
      'draft/setTurnoverControl',
    ),

  setDraftBufferSize: (v) =>
    set(
      (state) => {
        state.draftBufferSize = v;
      },
      false,
      'draft/setBufferSize',
    ),

  setDraftRetentionMethod: (v) =>
    set(
      (state) => {
        state.draftRetentionMethod = v;
      },
      false,
      'draft/setRetentionMethod',
    ),

  setDraftHoldingMethod: (v) =>
    set(
      (state) => {
        state.draftHoldingMethod = v;
      },
      false,
      'draft/setHoldingMethod',
    ),

  setDraftHoldingPeriods: (v) =>
    set(
      (state) => {
        state.draftHoldingPeriods = v;
      },
      false,
      'draft/setHoldingPeriods',
    ),

  setDraftReferenceScope: (v) =>
    set(
      (state) => {
        state.draftReferenceScope = v;
      },
      false,
      'draft/setReferenceScope',
    ),

  setDraftReferenceStatistic: (v) =>
    set(
      (state) => {
        state.draftReferenceStatistic = v;
      },
      false,
      'draft/setReferenceStatistic',
    ),

  loadStepIntoDraft: (step) =>
    set(
      (state) => {
        state.editingStepId = step.id;
        state.selectedFilterType = step.type;
        state.isAddingStep = true;
        state.draftCombinationMethod = step.combinationMethod;
        // Restored verbatim, not regenerated: the saved expression refers to
        // these aliases by name, so re-deriving them would break a formula the
        // user only opened to look at.
        state.draftFormula = step.formula ?? '';
        state.draftMetrics = step.metrics.map((m) => ({
          ...m,
          id: crypto.randomUUID(),
          configurations: { ...m.configurations },
        }));
        if (step.combinationMethod === 'formula') {
          assignMissingAliases(state.draftMetrics, state.draftFormula);
        }
        if (step.selection) {
          state.draftSelectionMethod = step.selection.method;
          state.draftSelectionLogic = step.selection.logic;
          state.draftSelectionValue = step.selection.value;
          if (step.selection.rankingDirection)
            state.draftRankingDirection = step.selection
              .rankingDirection as RankingDirection;
          if (step.selection.selectionSlice)
            state.draftSelectionSlice = step.selection
              .selectionSlice as SelectionSlice;
          if (step.selection.upperCutoff != null)
            state.draftUpperCutoff = step.selection.upperCutoff;
          if (step.selection.rankingMethod)
            state.draftRankingMethod = step.selection
              .rankingMethod as RankingMethod;
          if (step.selection.tieHandling)
            state.draftTieHandling = step.selection.tieHandling as TieHandling;
          if (step.selection.tieBreakMetric)
            state.draftTieBreakMetric = step.selection
              .tieBreakMetric as TieBreakMetric;
          if (step.selection.tieBreakPreference)
            state.draftTieBreakPreference = step.selection
              .tieBreakPreference as TieBreakPreference;
          if (step.selection.missingValuePolicy)
            state.draftMissingValuePolicy = step.selection
              .missingValuePolicy as MissingValuePolicy;
        }
        if (step.threshold) {
          state.draftThresholdOperator = step.threshold
            .operator as ThresholdOperator;
          state.draftThresholdValue = step.threshold.value;
          if (step.threshold.comparisonType)
            state.draftComparisonType = step.threshold
              .comparisonType as ComparisonType;
          if (step.threshold.missingValuePolicy)
            state.draftThresholdMissingPolicy = step.threshold
              .missingValuePolicy as ThresholdMissingPolicy;
          // A dynamic reference IS scope + statistic. Without these the editor
          // reopened on the defaults, so re-confirming silently rewrote the
          // step (starting_universe/mean → eligible_universe/median).
          if (step.threshold.referenceScope)
            state.draftReferenceScope = step.threshold
              .referenceScope as ReferenceScope;
          if (step.threshold.referenceStatistic)
            state.draftReferenceStatistic = step.threshold
              .referenceStatistic as ReferenceStatistic;
        }
        // Turnover buffer — same class of bug: handleConfirmStep ALWAYS writes
        // step.turnover from these three drafts, so leaving them at their
        // defaults meant editing a step (even just to rename a metric) silently
        // deleted its buffer — control back to 'None', size Number('') === 0.
        if (step.turnover) {
          state.draftTurnoverControl = step.turnover.control as TurnoverControl;
          state.draftBufferSize =
            step.turnover.size > 0 ? String(step.turnover.size) : '';
          state.draftRetentionMethod = step.turnover
            .retention as RetentionMethod;
        }
        // Overlapping holding — same class of bug as the buffer above: the
        // confirm path always writes step.holding, so leaving these at defaults
        // would silently drop the holding when a saved step is reopened.
        if (step.holding) {
          state.draftHoldingMethod = step.holding.method;
          state.draftHoldingPeriods =
            step.holding.periods > 0 ? String(step.holding.periods) : '';
        }
      },
      false,
      'draft/loadStepIntoDraft',
    ),

  resetDraft: () =>
    set(
      (state) => {
        state.editingStepId = null;
        state.selectedFilterType = null;
        state.selectionMethod = 'fixed';
        state.selectedCategory = 'all';
        state.metricsSearch = '';
        state.isAddingStep = false;
        state.draftMetrics = [];
        Object.assign(state, DRAFT_DEFAULTS);
        state.activeDraftLeg = null;
      },
      false,
      'draft/resetDraft',
    ),
});
